import * as React from 'react';

import { useClearDiagram } from 'src/containers/ProjectDiagram/hooks/useClearDiagram';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { ExtendedDiagram } from 'src/containers/ProjectDiagram/templates/extensions/diagram';
import { ModelConceptRelationship } from 'src/containers/ProjectDiagram/types/data';
import { addDeletedData } from 'src/containers/ProjectDiagram/utils/addDeletedData';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { removeModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/removeModelConceptRelationship';
import { uiSlice } from 'src/store/ui/ui.slice';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const useRemoveModelConceptRelationship = () => {
  const { projectId } = useRouterParams();
  const diagram = useDiagram();
  const clearDiagram = useClearDiagram();
  const dispatch = useReduxDispatch();

  return React.useCallback(
    async ({
      diagram: commitDiagram,
      key,
      removeAll,
    }: {
      diagram?: ExtendedDiagram;
      key: string;
      removeAll?: boolean;
    }) => {
      const diagramToUse = commitDiagram || diagram;
      if (!diagramToUse) {
        return;
      }

      const linkData = diagramToUse.model.linkDataArray.find(
        (linkData): linkData is ModelConceptRelationship =>
          isModelConceptRelationship(linkData) && linkData.key === key
      );

      if (!linkData) {
        return;
      }

      clearDiagram();

      const didCommit = await commitDiagramModel(
        diagramToUse,
        (model) => {
          if (!commitDiagram) {
            addDeletedData(
              removeAll
                ? model.linkDataArray.filter(
                    (data) =>
                      isModelConceptRelationship(data) &&
                      data.value === linkData.value
                  )
                : [linkData]
            )(model);
          }
          removeModelConceptRelationship(linkData, removeAll)(model);
        },
        {
          shouldCommit: !commitDiagram,
        }
      );

      if (didCommit) {
        dispatch(
          uiSlice.actions.removedKeysFromEntityDrawerHistory({
            projectId,
            entityValues: [linkData.value],
          })
        );
      }
    },
    [clearDiagram, diagram, dispatch, projectId]
  );
};
